/* AtomiQ v4.3: the policy pages (snippets/policy-shell.liquid). The policy text is the client's legal text and stays
   word for word. The snippet already prepared it on the server (ids on the h2s, numbered articles, intro roles,
   the table box, the table of contents), so the page is complete without this script. This script adds:
   - one section per h2 (the CSS spaces both states the same, so nothing moves), watched by an IntersectionObserver:
     the section being read lights its node in the route and the rail above it fills in (no scroll listener);
   - smooth jumps from the route that respect reduced motion and land the focus on the heading; on a phone the
     "Inhoud" box closes after a jump;
   - figures right-aligned in tables, and a chip on a cell that holds a word ("Gratis");
   - in the model withdrawal form (inside its closed details) the dotted lines drawn as field lines;
   - the print button (beside the table of contents on desktop). */

const root = document.querySelector('.pol');
const text = root?.querySelector('[data-pol-text]');

if (root && text) {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('is-motion-paused');
  const body = text.querySelector('.shopify-policy__body .rte') || text.querySelector('.rte') || text;

  /* ---------------------------------------------------------------- sections */
  const heads = [...body.querySelectorAll(':scope > h2')];
  const sections = heads.map((h) => {
    const sec = document.createElement('section');
    sec.className = 'pol-sec';
    if (h.id) sec.setAttribute('aria-labelledby', h.id);
    h.before(sec);
    let node = h;
    while (node && !(node !== h && node.tagName === 'H2')) {
      const next = node.nextElementSibling;
      sec.append(node);
      node = next;
    }
    return { sec, h };
  });

  /* ---------------------------------------------------------------- tables */
  for (const table of body.querySelectorAll('table')) {
    const labels = [...table.querySelectorAll('thead th')].map((th) => th.textContent.trim());
    table.querySelectorAll('thead th').forEach((th, i) => i > 0 && th.classList.add('is-num'));
    for (const row of table.querySelectorAll('tbody tr')) {
      [...row.children].forEach((cell, i) => {
        if (i === 0) return;
        cell.classList.add(/\d/.test(cell.textContent) ? 'is-num' : 'is-word');
      });
    }
    // A table wider than the column scrolls inside its box; then the box is a labelled, focusable region.
    const box = table.closest('.pol-table');
    if (box) {
      const check = () => {
        if (box.scrollWidth > box.clientWidth + 1) {
          box.tabIndex = 0;
          box.setAttribute('role', 'region');
          box.setAttribute('aria-label', labels.join(', '));
        } else {
          box.removeAttribute('tabindex');
          box.removeAttribute('role');
          box.removeAttribute('aria-label');
        }
      };
      check();
      new ResizeObserver(check).observe(box);
    }
  }

  /* ---------------------------------------------------------------- the model withdrawal form (as text) */
  // The snippet put the model text in a closed details under the document card. Its rows of dots are the lines of
  // the fields: the dots stay in the text (copied and printed as the client wrote them), on screen they are drawn
  // as one writing line.
  const model = body.querySelector('.pol-doc__model');
  if (model) {
    const fields = [];
    for (const p of model.querySelectorAll(':scope > p')) {
      if (p.textContent.includes('....')) {
        p.classList.add('pol-field');
        for (const node of [...p.childNodes]) {
          if (node.nodeType === 3 && node.textContent.includes('....')) {
            const line = document.createElement('span');
            line.className = 'pol-field__line';
            line.setAttribute('aria-hidden', 'true');
            line.textContent = node.textContent;
            node.replaceWith(line);
          } else if (node.nodeName === 'BR') node.remove();
        }
        fields.push(p);
      } else if (/^(Aan|To):/.test(p.textContent.trim())) p.classList.add('pol-paper__to');
    }
    if (fields.length) {
      const before = fields[0].previousElementSibling;
      if (before && !before.classList.contains('pol-paper__to')) before.classList.add('pol-paper__statement');
      fields[fields.length - 1].classList.add('pol-field--sign');
      fields[fields.length - 1].nextElementSibling?.classList.add('pol-paper__foot');
    }
    // Printing the policy prints the model text too.
    const details = model.closest('details');
    let wasOpen = false;
    window.addEventListener('beforeprint', () => {
      wasOpen = details.open;
      details.open = true;
    });
    window.addEventListener('afterprint', () => {
      details.open = wasOpen;
    });
  }

  /* ---------------------------------------------------------------- the route (table of contents) */
  const navs = [...root.querySelectorAll('[data-pol-toc]')];
  const phoneBox = root.querySelector('[data-pol-toc-m]');
  if (navs.length && sections.length > 1) {
    const lists = navs.map((nav) => [...nav.querySelectorAll('.pol-toc__item')]);
    let active = -1;
    const setActive = (i) => {
      if (i === active || i < 0) return;
      active = i;
      for (const items of lists) {
        items.forEach((li, j) => {
          li.classList.toggle('is-past', j < i);
          const a = li.querySelector('a');
          if (j === i) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      }
    };

    for (const items of lists) {
      items.forEach((li, i) => {
        const a = li.querySelector('a');
        a.addEventListener('click', (e) => {
          const target = sections[i]?.h;
          if (!target) return;
          e.preventDefault();
          if (phoneBox?.contains(a)) phoneBox.open = false;
          target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
          target.focus({ preventScroll: true });
          history.replaceState(null, '', a.hash);
          setActive(i);
        });
      });
    }

    // The section that covers a line at 30 % of the screen is the one being read.
    const visible = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) visible.set(e.target, e.isIntersecting);
        const i = sections.findIndex((s) => visible.get(s.sec));
        if (i >= 0) setActive(i);
      },
      { rootMargin: '-30% 0px -69% 0px' }
    );
    sections.forEach((s) => io.observe(s.sec));
    const fromHash = sections.findIndex((s) => `#${s.h.id}` === decodeURIComponent(location.hash));
    setActive(fromHash >= 0 ? fromHash : 0);
  }

  /* ---------------------------------------------------------------- print */
  const print = root.querySelector('[data-pol-print]');
  if (print && typeof window.print === 'function') {
    print.hidden = false;
    print.addEventListener('click', () => window.print());
  }
}
